import type * as React from 'react';

/**
 * The DrawImage interface represents an image to be drawn on a canvas.
 * It includes the necessary properties to position, rotate, scale, and zoom the image.
 *
 * @interface DrawImage
 * @property {React.MutableRefObject<null>} canvasRef - A React ref object for the canvas element to draw the image on.
 * @property {React.MutableRefObject<null>} imageRef - A React ref object for the image element to be drawn.
 * @property {Object} position - The position of the image on the canvas, specified as x and y coordinates.
 * @property {number} position.x - The x-coordinate of the image's position.
 * @property {number} position.y - The y-coordinate of the image's position.
 * @property {number} rotation - The rotation angle of the image in degrees.
 * @property {Object} size - The size dimensions of the image.
 * @property {number} size.width - The width of the image.
 * @property {number} size.height - The height of the image.
 * @property {number} zoom - The zoom level or scale factor of the image.
 */
export interface DrawImage {
  canvasRef: React.MutableRefObject<any>;
  imageRef: React.MutableRefObject<any>;
  position: { x: number; y: number };
  rotation: number;
  size: { width: number; height: number };
  zoom: number;
}

const drawImage = ({
  canvasRef,
  imageRef,
  position,
  rotation,
  size,
  zoom,
}: DrawImage) => {
  const canvas = canvasRef.current;
  const ctx = canvas.getContext('2d');
  if (!canvas || !ctx || !imageRef.current) return;
  const { width, height } = size;

  ctx.clearRect(0, 0, width, height);
  ctx.save();
  ctx.beginPath();
  ctx.arc(width / 2, height / 2, Math.min(width, height) / 2, 0, Math.PI * 2);
  ctx.clip();
  ctx.translate(width / 2, height / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-width / 2, -height / 2);
  ctx.drawImage(
    imageRef.current,
    position.x,
    position.y,
    width * zoom,
    height * zoom,
    0,
    0,
    width,
    height,
  );
  ctx.restore();
};

export default drawImage;
